import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientRecordDto } from "../types/patientRecord";
import { patientRecordService } from "../services/patientRecordService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientRecords = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: records = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientRecordDto[]>(
    patientId ? `patient-records-${patientId}` : "patient-records-all",
    async (signal) => {
      if (patientId) {
        return patientRecordService.getByPatientId(patientId, signal);
      }
      return patientRecordService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newPatientRecord: any) => {
    const payload = patientId 
      ? { ...newPatientRecord, patientId: Number(patientId) } 
      : newPatientRecord;
      
    return patientRecordService.create(payload as any);
  });

  // Mutation για Update (με μοναδικό όνομα execution)
  const {
    execute: updateRecordExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(async ({ id, data }: { id: number; data: any }) => {
    return patientRecordService.update(id, data as any);
  });

  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientRecordService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientRecord = async (newPatientRecord: any): Promise<void> => {
    try {
      await createExecution(newPatientRecord);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το αρχείο ασθενούς αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updatePatientRecord = async (id: number, updatedData: any): Promise<void> => {
    try {
      await updateRecordExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση του αρχείου ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης αρχείου";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deletePatientRecords = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientRecords: records, 
    allRecords: records,    
    isLoading: isFetching || isCreating || isDeleting || isUpdating,
    error: getError || createError || deleteError || updateError,
    fetchPatientRecords: () => refetch(true),
    fetchRecords: () => refetch(true),
    createPatientRecord,
    updatePatientRecord,
    deletePatientRecords,
    modalState,
    closeModal,
  };
};